import type { Journey, JourneyTask } from "../../types/journey";
import type { StepStatePayload } from "../journeyProgress/schemas";
import { deriveTaskDateWindow } from "./deriveTaskDateWindow";

/**
 * M011/S04 T02: pure adapter that takes the merged view model coming out of
 * useJourneyProgress and emits the flat list of task "occurrences" the Track
 * page renders in its calendar + Coming Up sections.
 *
 * Each emitted occurrence is per-(step, task) and carries the step's date
 * window (start/end ISO). The Track calendar buckets each occurrence into
 * the cell whose date matches `startIso`; Coming Up surfaces the occurrence
 * once. Per-day expansion across the full window is intentionally not done
 * here — the design shows one chip per task within its window, not a chip on
 * every day.
 *
 * Filtering rules (mirror Track product copy):
 * - Only emit occurrences for steps whose progress status is `in_progress`.
 *   Completed and not_started steps are silent.
 * - Within an in-progress step, drop any task whose id is in
 *   `completedTaskIds`. `useJourneyProgress` already merges per-task
 *   `taskProgress` rows whose status is `complete` into `completedTaskIds`,
 *   so checking the set is sufficient and the adapter doesn't need the
 *   `taskProgress` map.
 * - The same date window (from `deriveTaskDateWindow`) is applied to every
 *   task under a step. This matches the design: Track shows a per-step
 *   window, not per-task deadlines.
 *
 * Pure: no React, no I/O, no Date.now. Caller passes `now`.
 */

export type JourneyTaskOccurrenceSource = "journey";

export interface JourneyTaskOccurrence {
	id: string;
	taskId: string;
	stepId: string;
	startIso: string;
	endIso: string;
	title: string;
	status: "not_started" | "in_progress";
	source: JourneyTaskOccurrenceSource;
	/** Full task object from the journey JSON — used by JourneyTaskDetailSheet for rich modal content */
	task: JourneyTask;
	/** M011 S07 T11 — forwarded from JourneyTask.taskDomain for per-domain coloring. */
	taskDomain?: string;
}

export interface SelectTaskOccurrencesInput {
	journey: Journey | null;
	stepStates: Map<string, StepStatePayload>;
	completedTaskIds: Set<string>;
	journeyStartedAt: string | null | undefined;
	now: Date;
}

export function selectJourneyTaskOccurrences(
	input: SelectTaskOccurrencesInput,
): JourneyTaskOccurrence[] {
	const { journey, stepStates, completedTaskIds, journeyStartedAt, now } =
		input;
	if (!journey) return [];

	const occurrences: JourneyTaskOccurrence[] = [];

	for (const step of journey.steps) {
		const stepState = stepStates.get(step.id);
		if (stepState?.status !== "in_progress") continue;

		const window = deriveTaskDateWindow(step, journeyStartedAt, now);

		for (const task of step.tasks) {
			if (completedTaskIds.has(task.id)) continue;

			occurrences.push({
				id: `${step.id}:${task.id}`,
				taskId: task.id,
				stepId: step.id,
				startIso: window.startIso,
				endIso: window.endIso,
				title: task.title,
				status: "in_progress",
				source: "journey",
				task,
				taskDomain: task.taskDomain,
			});
		}
	}

	return occurrences;
}
